const INLINE_DISPOSITION_PARAM = "response-content-disposition";
const INLINE_DISPOSITION_VALUE = "inline";
const ATTACHMENT_DISPOSITION_VALUE = "attachment";

/** Explicit Storage metadata for new PDF uploads. Existing objects are unchanged. */
export const PDF_UPLOAD_METADATA = {
  contentType: "application/pdf",
  contentDisposition: "inline",
};

const ACCEPTED_PDF_MIME_TYPES = new Set([
  "application/pdf",
  "application/x-pdf",
  "application/octet-stream",
  "",
]);

export function isPdfFileName(fileName) {
  return typeof fileName === "string" && fileName.toLowerCase().endsWith(".pdf");
}

/**
 * Accept PDF uploads when the filename ends with .pdf and the browser reports a
 * common PDF or generic MIME type (including empty type on some mobile devices).
 */
export function isAcceptedPdfUpload(file) {
  if (!file || !isPdfFileName(file.name)) {
    return false;
  }
  return ACCEPTED_PDF_MIME_TYPES.has((file.type || "").toLowerCase());
}

/**
 * Append Google Cloud Storage inline disposition override so browsers prefer
 * rendering in an iframe over downloading.
 */
export function ensureInlinePdfUrl(url) {
  if (!url || typeof url !== "string") {
    return null;
  }

  try {
    const baseOrigin =
      typeof window !== "undefined" ? window.location.origin : "https://example.com";
    const parsed = new URL(url, baseOrigin);

    if (!parsed.searchParams.get(INLINE_DISPOSITION_PARAM)) {
      parsed.searchParams.set(INLINE_DISPOSITION_PARAM, INLINE_DISPOSITION_VALUE);
    }

    return parsed.toString();
  } catch {
    return url;
  }
}

/** Build a FilePicker grid thumbnail URL (first page, fit-to-view, chrome hidden). */
export function buildFilePickerPdfThumbUrl(url) {
  const inlineUrl = ensureInlinePdfUrl(url);
  if (!inlineUrl) {
    return null;
  }

  const thumbHash = "page=1&view=Fit&toolbar=0&navpanes=0&scrollbar=0";

  try {
    const baseOrigin =
      typeof window !== "undefined" ? window.location.origin : "https://example.com";
    const parsed = new URL(inlineUrl, baseOrigin);
    parsed.hash = thumbHash;
    return parsed.toString();
  } catch {
    const base = inlineUrl.split("#")[0];
    return `${base}#${thumbHash}`;
  }
}

/** Build a download-oriented URL from the same Storage object. */
export function getPdfDownloadUrl(url) {
  if (!url || typeof url !== "string") {
    return null;
  }

  try {
    const baseOrigin =
      typeof window !== "undefined" ? window.location.origin : "https://example.com";
    const parsed = new URL(url, baseOrigin);
    parsed.searchParams.set(INLINE_DISPOSITION_PARAM, ATTACHMENT_DISPOSITION_VALUE);
    return parsed.toString();
  } catch {
    return url;
  }
}

export function isPdfDiagnosticsEnabled() {
  if (process.env.NODE_ENV !== "production") {
    return true;
  }

  if (typeof window === "undefined") {
    return false;
  }

  try {
    return window.localStorage?.getItem("llp-pdf-debug") === "1";
  } catch {
    return false;
  }
}

/** Lightweight, opt-in diagnostics for PDF load troubleshooting. */
export function logPdfDiagnostic(context, event, detail = {}) {
  if (!isPdfDiagnosticsEnabled()) {
    return;
  }

  console.debug("[Lesson Launchpad PDF]", {
    ts: new Date().toISOString(),
    context,
    event,
    ...detail,
  });
}
